import moment from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Skeleton } from '../../../../../components/ui/Skeleton';
import {
  ReservationFormValuesProps,
  ReservationGeneral
} from '../../../../../models/owner-portal/reservationGeneral';
import { useGetRTKQReservationsQuery } from '../../../../../redux/rtkQuery/apiSlice';
import { ReservationDatePicker } from '../../ReservationDatePicker/ReservationDatePicker';
import { brandConfig } from '../../../../../config';

//--------- Step Three form

const checkInRange = (
  start: moment.Moment,
  end: moment.Moment,
  blockedDates: ReservationGeneral[]
) => {
  for (let i = 0; i < blockedDates.length; i++) {
    const blockedDate = blockedDates[i];
    if (
      start.isSameOrAfter(blockedDate.check_in_ISO) &&
      start.isBefore(blockedDate.check_out_ISO) &&
      end.isAfter(blockedDate.check_in_ISO) &&
      end.isSameOrBefore(blockedDate.check_in_ISO)
    ) {
      return false;
    }
  }
  return true;
};

interface DatePickerFormI {
  ignoredOwnerReservation?: ReservationGeneral;
  listingId: string;
  title?: string;
  disablePortal?: boolean;
}

const PRELOAD_DURATION_MS = 90 * 24 * 60 * 60 * 1000; // 90 days
export const datePickerStartDate = new Date(Date.now() - PRELOAD_DURATION_MS);

export default function DatePickerForm(props: DatePickerFormI) {
  const { ignoredOwnerReservation, listingId, title, disablePortal } = props;

  const {
    data: reservationData,
    isFetching,
    isSuccess
  } = useGetRTKQReservationsQuery({
    listingId: listingId,
    startDate: datePickerStartDate,
    includePending: true
  });

  const reservationsRelatedToListing = useMemo(() => {
    if (!isSuccess) {
      return [];
    }
    const listingReservations = reservationData.filter(
      (reservationRelatedToListing) => reservationRelatedToListing.listing_id === listingId
    );
    return listingReservations;
  }, [reservationData, listingId, isSuccess]);

  const blockedDates = useMemo(() => {
    return !isSuccess
      ? []
      : ignoredOwnerReservation
      ? reservationsRelatedToListing.filter(({ id }) => id !== ignoredOwnerReservation.id)
      : reservationsRelatedToListing;
  }, [ignoredOwnerReservation, isSuccess, reservationsRelatedToListing]);

  const {
    watch,
    setValue,
    setError,
    formState: { errors }
  } = useFormContext<ReservationFormValuesProps>();
  const { check_in, check_out, listing } = watch();

  const checkIn = useMemo(() => (check_in ? moment(check_in) : null), [check_in]);
  const checkOut = useMemo(() => (check_out ? moment(check_out) : null), [check_out]);

  // Check if existing reservation has a past or today check-in date
  const isExistingReservationWithPastCheckIn =
    ignoredOwnerReservation &&
    moment(ignoredOwnerReservation.check_in_ISO).isSameOrBefore(moment(), 'day');

  // For future reservations in edit mode, don't pass existingReservation to clear the visual range
  const shouldPassExistingReservation =
    ignoredOwnerReservation && isExistingReservationWithPastCheckIn;

  const setCheckIn = useCallback(
    (date: moment.Moment | null) => {
      setValue('check_in', date ? date.toDate() : null);
    },
    [setValue]
  );
  const setCheckOut = useCallback(
    (date: moment.Moment | null) => {
      setValue('check_out', date ? date.toDate() : null);
    },
    [setValue]
  );

  useEffect(() => {
    if (!checkIn) {
      return;
    }
    if (!checkOut) {
      return;
    }
    if (!checkInRange(checkIn, checkOut, blockedDates)) {
      setCheckIn(null);
      setCheckOut(null);
      setError('check_out', { message: 'The selected dates are not available.' });
    }
  }, [blockedDates, checkIn, checkOut, setCheckIn, setCheckOut, setError]);

  return (
    <div className="flex flex-col gap-4">
      <div>
        {isFetching ? (
          <h5 className="text-xl font-semibold">Loading dates...</h5>
        ) : (
          isSuccess && (
            <h5 className="text-xl font-semibold">
              {title
                ? title
                : `Please select dates for ${
                    listing?.name_detailed || reservationsRelatedToListing[0].name_detailed
                  }`}
            </h5>
          )
        )}
      </div>
      {isFetching && <Skeleton className="w-[275px] h-[55px]" />}
      {!isFetching && (
        <ReservationDatePicker
          startDateName="check_in"
          endDateName="check_out"
          isEditingMode={!!ignoredOwnerReservation}
          existingReservation={
            shouldPassExistingReservation
              ? {
                  check_in: moment(ignoredOwnerReservation!.check_in_ISO)
                    .tz(brandConfig.timezone)
                    .toDate(),
                  check_out: moment(ignoredOwnerReservation!.check_out_ISO)
                    .tz(brandConfig.timezone)
                    .toDate(),
                  sfID: ignoredOwnerReservation!.id
                }
              : undefined
          }
          blockedDates={blockedDates.map((res) => {
            const checkInAsTimezone = moment(res.check_in_ISO).tz(brandConfig.timezone).toDate();
            const checkOutAsTimezone = moment(res.check_out_ISO).tz(brandConfig.timezone).toDate();

            return { check_in: checkInAsTimezone, check_out: checkOutAsTimezone, sfID: res.id };
          })}
          disablePortal={disablePortal}
        />
      )}
      {errors?.check_out && (
        <p className="text-sm text-destructive mt-1">{errors.check_out.message}</p>
      )}
      {isExistingReservationWithPastCheckIn && (
        <p className="text-sm text-muted-foreground mt-1">⚠️ Check-in date is locked.</p>
      )}
    </div>
  );
}
